import React, { useEffect, useMemo, useState } from 'react';
import { CELEBRATION_PHOTOS } from './CelebrationCard';

const GOLD = 'linear-gradient(180deg, #f6e7b4 0%, #d9b357 42%, #b98d2e 58%, #e8cc7a 100%)';
const CONFETTI_COLORS = ['#f6e7b4', '#d9b357', '#b98d2e', '#ffffff', '#e8cc7a'];

const css = `
@keyframes rsCelebrateIn {
  0% { transform: translateZ(0) scale(0.25); opacity: 0; filter: blur(6px); }
  55% { transform: scale(1.08); opacity: 1; filter: blur(0); }
  72% { transform: scale(0.97); }
  86% { transform: scale(1.02); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes rsMsgIn {
  0% { transform: translateY(18px) scale(0.9); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}
@keyframes rsBurst {
  0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  70% { opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)); opacity: 0; }
}
@keyframes rsDim {
  0% { background: rgba(0,0,0,0); }
  100% { background: rgba(0,0,0,0.72); }
}
@media (prefers-reduced-motion: reduce) {
  .rs-anim, .rs-confetti, .rs-msg, .rs-dim { animation: none !important; }
}
`;

export default function CelebrationOverlay({ onClose }) {
  const photo = useMemo(() => (
    CELEBRATION_PHOTOS.length
      ? CELEBRATION_PHOTOS[Math.floor(Math.random() * CELEBRATION_PHOTOS.length)]
      : ''
  ), []);

  // Confetti pieces are generated once so they stay put across renders.
  const pieces = useMemo(() => Array.from({ length: 220 }, (_, i) => {
    // About half burst out from the center; the rest sweep across from both sides.
    const fromSide = i % 2 === 1;
    const fromLeft = i % 4 === 1;
    return {
      id: i,
      left: fromSide ? (fromLeft ? -4 : 104) : 50 + (Math.random() - 0.5) * 20,
      top: fromSide ? 10 + Math.random() * 60 : 40 + (Math.random() - 0.5) * 12,
      dx: fromSide
        ? `${(fromLeft ? 1 : -1) * (110 + Math.random() * 30)}vw`
        : `${(Math.random() - 0.5) * 110}vw`,
      dy: `${20 + Math.random() * 80}vh`,
      rot: `${(Math.random() - 0.5) * 1080}deg`,
      size: 6 + Math.random() * 9,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: fromSide ? Math.random() * 0.9 : Math.random() * 0.4,
      duration: 2.6 + Math.random() * 1.8,
      round: i % 3 === 0,
    };
  }), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Under contract celebration"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box', overflow: 'hidden' }}
    >
      <style>{css}</style>
      <div className="rs-dim" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.72)', animation: 'rsDim 0.4s ease-out forwards' }} onClick={onClose} />

      {/* Confetti burst */}
      <div className="rs-confetti" aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {pieces.map((p) => (
          <span
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: p.size,
              height: p.round ? p.size : p.size * 0.45,
              borderRadius: p.round ? '50%' : '1px',
              background: p.color,
              boxShadow: '0 0 6px rgba(246,231,180,0.8)',
              '--dx': p.dx,
              '--dy': p.dy,
              '--rot': p.rot,
              animation: `rsBurst ${p.duration}s cubic-bezier(.2,.7,.3,1) ${p.delay}s forwards`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* Card */}
      <div style={{ position: 'relative', width: 'min(92vw, 460px)', maxHeight: '96vh', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close celebration"
          style={{ position: 'absolute', top: -8, right: -8, zIndex: 2, width: 40, height: 40, borderRadius: '50%', border: '1px solid #D4AF37', background: '#000', color: '#D4AF37', fontSize: 22, lineHeight: '38px', cursor: 'pointer' }}
        >
          ×
        </button>

        <div className="rs-anim" style={{ animation: 'rsCelebrateIn 1.1s cubic-bezier(.22,.8,.3,1) forwards', opacity: 0, width: '100%' }}>
          {photo ? (
            <img
              src={photo}
              alt="Congratulations"
              style={{ width: '100%', maxHeight: '62vh', objectFit: 'contain', borderRadius: '12px', border: '2px solid #D4AF37', boxShadow: '0 20px 60px rgba(212,175,55,0.35)' }}
            />
          ) : null}
        </div>

        <div className="rs-msg" style={{ animation: 'rsMsgIn 0.6s ease-out 0.7s forwards', opacity: 0, marginTop: 18, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 'clamp(22px, 5vw, 32px)', lineHeight: 1.15, letterSpacing: '0.02em', color: '#ffffff' }}>
          BOOM! <span style={{ background: GOLD, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>ANOTHER ONE UNDER CONTRACT!</span>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{ marginTop: 20, width: '100%', maxWidth: 360, padding: '16px 20px', border: '1px solid #b8903a', borderRadius: 8, background: GOLD, color: '#1a1a1a', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em', cursor: 'pointer', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 14px rgba(140,109,31,0.45)' }}
        >
          LET’S GET TO WORK! →
        </button>
      </div>
    </div>
  );
}
